import { type NextRequest } from 'next/server'

interface Config {
  frontendUrl: string
  backendUrl: string

  /** URL для API */
  apiUrl: string

  /** Proxy Api URL */
  proxyUrlClient: string

  /** Proxy Api URL */
  proxyUrlServer: string

  /**максимальное время жизни jwt токена */
  jwtMaxAge: number

  jwtCookieName: string
  switchToUserCookieName: string
  refreshTokenCookieName: string

  isDev: boolean
  agGridLicence: string
  isProd: boolean
}

const defaultConfig: Config = {
  frontendUrl: process.env.NEXT_PUBLIC_FRONTEND_URL!,
  backendUrl: process.env.NEXT_PUBLIC_API_BASEURL!,
  apiUrl: process.env.NEXT_PUBLIC_API_BASEURL!,
  proxyUrlClient: '/api/server',
  proxyUrlServer: process.env.NEXT_PUBLIC_PROXY_API_BASEURL!,
  jwtMaxAge: 1800, // 30 минут
  jwtCookieName: 'jwt',
  switchToUserCookieName: 'sjwt',
  refreshTokenCookieName: 'REFRESH_TOKEN',
  agGridLicence:
    'Using_this_{AG_Grid}_Enterprise_key_{AG-086539}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{EVIRMA}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{EVIRMA}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{EVIRMA}_need_to_be_licensed___{EVIRMA}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{15_April_2026}____[v3]_[01]_MTc3NjIwNzYwMDAwMA==3c6c96aef283fe84cdec70fb2dab539a',
  isDev: false,
  isProd: true,
}

export const config = assignDefined<Config>(defaultConfig, {
  frontendUrl: process.env.FRONTEND_URL,
  apiUrl: process.env.NEXT_PUBLIC_API_BASEURL,
  backendUrl: process.env.NEXT_PUBLIC_API_BASEURL,

  proxyUrlServer: process.env.NEXT_PROXY_API_BASEURL,

  isDev: process.env.NODE_ENV === 'development',
  isProd: process.env.NODE_ENV !== 'development',
})

function assignDefined<T = any>(target: T, ...sources: Partial<T>[]): T {
  for (const source of sources) {
    for (const key in source) {
      if (source[key] === undefined) {
        continue
      }
      target[key] = source[key]!
    }
  }
  return target
}

function formatDate(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0')

  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1) // 0..11
  const day = pad(date.getDate())

  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const seconds = pad(date.getSeconds())

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

export function log(...args: unknown[]): void {
  const now = formatDate(new Date())
  console.log(`[${now}]`, ...args)
}

export async function safeParseReqBody(req: NextRequest) {
  try {
    const text = await req.text()
    if (!text) {
      return undefined
    }
    const json = JSON.parse(text)
    if (json) {
      return JSON.stringify(json)
    }
  } catch (error) {
    log('Error:', error)
    return undefined
  }
}

export function getResponseHeaders(response: Response): Headers {
  const responseHeaders = new Headers({
    'content-type': response.headers.get('content-type') ?? 'application/json',
  })

  if (response.headers.has('set-cookie')) {
    responseHeaders.append('set-cookie', response.headers.get('set-cookie')!)
  }

  // Через прокси идут и файлы, загруженные пользователями: браузер не должен угадывать их тип.
  responseHeaders.set('x-content-type-options', 'nosniff')

  // Без content-disposition браузер назовёт скачанный файл по адресу, а не по его настоящему имени.
  const forwardedHeaders = [
    'content-disposition',
    'x-ratelimit-limit',
    'x-ratelimit-remaining',
    'x-ratelimit-reset',
    'x-ratelimit-reset-datetime',
    'x-ratelimit-used',
  ]

  for (const header of forwardedHeaders) {
    if (response.headers.has(header)) {
      responseHeaders.append(header, response.headers.get(header)!)
    }
  }

  return responseHeaders
}
